import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { invoicesAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const statusMap = { paid: 'badge-green', pending: 'badge-yellow', partially_paid: 'badge-orange', draft: 'badge-gray', cancelled: 'badge-red', converted: 'badge-blue' };

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'cash', reference_number: '', notes: '' });

  useEffect(() => {
    invoicesAPI.get(id)
      .then(r => setInvoice(r.data))
      .catch(() => { toast.error('Invoice not found'); navigate('/invoices'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handlePayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Enter valid amount'); return; }
    try {
      await invoicesAPI.recordPayment(id, payForm);
      toast.success('Payment recorded successfully');
      setShowPayment(false);
      const res = await invoicesAPI.get(id);
      setInvoice(res.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Payment failed'); }
  };

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${invoice.invoice_number}</title>
      <style>
        body { font-family: 'Segoe UI', sans-serif; font-size: 12px; margin: 20px; color: #000; }
        table { width: 100%; border-collapse: collapse; margin: 10px 0; }
        th, td { border: 1px solid #333; padding: 5px 8px; text-align: left; font-size: 11px; }
        th { background: #e9ecef; font-weight: 700; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .font-bold { font-weight: 700; }
        .inv-header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px; }
        .inv-title { font-size: 18px; font-weight: 700; text-align: center; margin: 8px 0; text-transform: uppercase; }
        .gst-type { background: ${invoice?.invoice_type === 'gst_invoice' ? '#198754' : '#e8590c'}; color: white; padding: 3px 12px; display: inline-block; font-size: 11px; font-weight: 700; border-radius: 3px; }
        .section { margin-bottom: 8px; }
        .summary-row { display: flex; justify-content: space-between; padding: 3px 0; }
        .footer { border-top: 1px solid #999; margin-top: 16px; padding-top: 8px; }
        @media print { body { margin: 10px; } }
      </style>
    </head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    win.print();
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await invoicesAPI.downloadPDF(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${inv.invoice_number.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF generation failed'); }
  };

  const handleViewPDF = () => {
    const token = localStorage.getItem('token');
    const url = `${invoicesAPI.getPDFUrl(id)}?token=${token}`;
    window.open(url, '_blank');
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!invoice) return <div className="empty-state"><h3>Invoice not found</h3></div>;

  const inv = invoice;
  const isProforma = inv.invoice_type === 'proforma';
  const isIGST = inv.is_igst;

  return (
    <div>
      <div className="page-header no-print">
        <div>
          <h1>{inv.invoice_number}</h1>
          <div className="flex gap-2 items-center mt-2">
            <span className={`badge ${isProforma ? 'badge-orange' : 'badge-green'}`}>{isProforma ? 'Proforma Invoice' : 'GST Tax Invoice'}</span>
            <span className={`badge ${statusMap[inv.payment_status]}`}>{inv.payment_status?.replace('_',' ')}</span>
          </div>
        </div>
        <div className="page-actions">
          {isProforma && inv.payment_status !== 'converted' && (
            <Link to={`/invoices/new?convert=${inv.id}`} className="btn btn-success">Convert to GST Invoice</Link>
          )}
          {!isProforma && inv.payment_status !== 'paid' && inv.payment_status !== 'cancelled' && (
            <button onClick={() => { setShowPayment(true); setPayForm(f => ({ ...f, amount: inv.balance_amount })); }} className="btn btn-primary">Record Payment</button>
          )}
          <button onClick={handleDownloadPDF} className="btn btn-accent">⬇ Download PDF</button>
          <button onClick={handlePrint} className="btn btn-secondary">🖨 Print</button>
          <button onClick={() => navigate('/invoices')} className="btn btn-outline">Back</button>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPayment(false)}>
          <div className="modal modal-sm">
            <div className="modal-header"><h2>Record Payment</h2><button onClick={() => setShowPayment(false)} className="btn btn-icon">✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">Amount (₹) — Balance: ₹{fmt(inv.balance_amount)}</label>
                <input type="number" step="0.01" className="form-input" value={payForm.amount} onChange={e => setPayForm(f => ({...f, amount: e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Payment Mode</label>
                <select className="form-select" value={payForm.payment_mode} onChange={e => setPayForm(f => ({...f, payment_mode: e.target.value}))}>
                  <option value="cash">Cash</option><option value="upi">UPI</option><option value="card">Card</option>
                  <option value="neft">NEFT/RTGS</option><option value="cheque">Cheque</option>
                </select></div>
              <div className="form-group"><label className="form-label">Reference / Transaction #</label>
                <input className="form-input" value={payForm.reference_number} onChange={e => setPayForm(f => ({...f, reference_number: e.target.value}))} placeholder="UTR, Cheque No, etc." /></div>
            </div>
            <div className="modal-footer"><button onClick={() => setShowPayment(false)} className="btn btn-secondary">Cancel</button><button onClick={handlePayment} className="btn btn-primary">Confirm Payment</button></div>
          </div>
        </div>
      )}

      {/* Printable Invoice */}
      <div className="card">
        <div className="card-body" ref={printRef}>
          <div className="invoice-print">
            {/* Header */}
            <div className="inv-header" style={{ display:'flex', justifyContent:'space-between', borderBottom:'2px solid #000', paddingBottom:12 }}>
              <div>
                <h2 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>{inv.company_name || inv.company_legal_name}</h2>
                <div style={{ fontSize:11, color:'#555' }}>
                  {inv.company_address1 && <div>{inv.company_address1}</div>}
                  <div>{[inv.company_city, inv.company_state, inv.company_pincode].filter(Boolean).join(', ')}</div>
                  {inv.company_phone && <div>Phone: {inv.company_phone}</div>}
                  {inv.company_email && <div>Email: {inv.company_email}</div>}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:11 }}>
                  <div><strong>GSTIN:</strong> {inv.company_gstin}</div>
                  <div><strong>Drug Lic. No:</strong> {inv.company_dl || inv.dl_number}</div>
                  <div><strong>State:</strong> {inv.company_state} ({inv.company_state_code})</div>
                </div>
              </div>
            </div>

            <div className="inv-title">
              <span className="gst-type">{isProforma ? 'PROFORMA INVOICE' : 'TAX INVOICE'}</span>
            </div>

            {/* Invoice + Customer Details */}
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12, gap:20 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, fontWeight:700, marginBottom:4, textTransform:'uppercase', color:'#666' }}>Bill To:</div>
                <div style={{ fontWeight:700 }}>{inv.customer_name}</div>
                {inv.customer_legal_name && inv.customer_legal_name !== inv.customer_name && <div>{inv.customer_legal_name}</div>}
                <div style={{ fontSize:11 }}>
                  {inv.customer_address1 && <div>{inv.customer_address1}</div>}
                  {inv.customer_address2 && <div>{inv.customer_address2}</div>}
                  <div>{[inv.customer_city, inv.customer_state, inv.customer_pincode].filter(Boolean).join(', ')}</div>
                  {inv.customer_gstin && <div><strong>GSTIN:</strong> {inv.customer_gstin}</div>}
                  {inv.customer_dl && <div><strong>Drug Lic.:</strong> {inv.customer_dl}</div>}
                </div>
              </div>
              <div style={{ minWidth:220 }}>
                <table style={{ border:'none' }}>
                  <tbody>
                    <tr><td style={{ border:'none', fontWeight:600, padding:'2px 8px 2px 0' }}>Invoice No:</td><td style={{ border:'none', padding:'2px 0' }}>{inv.invoice_number}</td></tr>
                    <tr><td style={{ border:'none', fontWeight:600, padding:'2px 8px 2px 0' }}>Date:</td><td style={{ border:'none', padding:'2px 0' }}>{new Date(inv.invoice_date).toLocaleDateString('en-IN')}</td></tr>
                    {inv.due_date && <tr><td style={{ border:'none', fontWeight:600, padding:'2px 8px 2px 0' }}>Due Date:</td><td style={{ border:'none', padding:'2px 0' }}>{new Date(inv.due_date).toLocaleDateString('en-IN')}</td></tr>}
                    <tr><td style={{ border:'none', fontWeight:600, padding:'2px 8px 2px 0' }}>Place of Supply:</td><td style={{ border:'none', padding:'2px 0' }}>{inv.place_of_supply} ({inv.supply_state_code})</td></tr>
                    {inv.proforma_ref_number && <tr><td style={{ border:'none', fontWeight:600, padding:'2px 8px 2px 0' }}>Ref. Proforma:</td><td style={{ border:'none', padding:'2px 0' }}>{inv.proforma_ref_number}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Items Table */}
            <table>
              <thead>
                <tr>
                  <th style={{width:30}}>#</th>
                  <th>Product</th>
                  <th>HSN</th>
                  <th>Sch.</th>
                  <th>Batch</th>
                  <th>Expiry</th>
                  <th className="text-center">Qty</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">MRP</th>
                  <th className="text-right">Disc%</th>
                  <th className="text-right">Taxable</th>
                  {!isIGST ? <><th className="text-right">CGST</th><th className="text-right">SGST</th></> : <th className="text-right">IGST</th>}
                  <th className="text-right font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {(inv.items || []).map((item, i) => (
                  <tr key={item.id}>
                    <td>{i + 1}</td>
                    <td><div style={{fontWeight:600}}>{item.product_name}</div>{item.generic_name && <div style={{fontSize:10, color:'#666'}}>{item.generic_name}</div>}</td>
                    <td>{item.hsn_code}</td>
                    <td>{item.drug_schedule !== 'none' ? item.drug_schedule : '-'}</td>
                    <td>{item.batch_number || '-'}</td>
                    <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-IN',{month:'short',year:'2-digit'}) : '-'}</td>
                    <td className="text-center">{item.quantity}{item.free_quantity > 0 ? `+${item.free_quantity}` : ''}</td>
                    <td className="text-right">{fmt(item.unit_price)}</td>
                    <td className="text-right">{fmt(item.mrp)}</td>
                    <td className="text-right">{Number(item.discount_pct) > 0 ? `${item.discount_pct}%` : '-'}</td>
                    <td className="text-right">{fmt(item.taxable_amount)}</td>
                    {!isIGST ? (
                      <><td className="text-right">{fmt(item.cgst_amount)}</td><td className="text-right">{fmt(item.sgst_amount)}</td></>
                    ) : (
                      <td className="text-right">{fmt(item.igst_amount)}</td>
                    )}
                    <td className="text-right font-bold">{fmt(item.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Summary */}
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
              <div style={{ minWidth:300 }}>
                <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>Subtotal:</span><span>₹{fmt(inv.subtotal)}</span></div>
                {Number(inv.discount_amount) > 0 && <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>Discount:</span><span>-₹{fmt(inv.discount_amount)}</span></div>}
                <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>Taxable Amount:</span><span>₹{fmt(inv.taxable_amount)}</span></div>
                {!isIGST ? (
                  <><div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>CGST:</span><span>₹{fmt(inv.cgst_amount)}</span></div>
                  <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>SGST:</span><span>₹{fmt(inv.sgst_amount)}</span></div></>
                ) : (
                  <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0' }}><span>IGST:</span><span>₹{fmt(inv.igst_amount)}</span></div>
                )}
                {Number(inv.round_off) !== 0 && <div className="summary-row" style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:11 }}><span>Round Off:</span><span>{inv.round_off}</span></div>}
                <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', fontSize:16, fontWeight:700, borderTop:'2px solid #000', marginTop:4 }}>
                  <span>Grand Total:</span><span>₹{fmt(inv.total_amount)}</span>
                </div>
              </div>
            </div>

            {/* Amount in Words */}
            {inv.amount_in_words && (
              <div style={{ marginTop:8, padding:'6px 0', borderTop:'1px solid #ccc', fontSize:11, fontStyle:'italic' }}>
                <strong>Amount in Words:</strong> {inv.amount_in_words}
              </div>
            )}

            {/* Bank Details */}
            {inv.bank_name && (
              <div style={{ marginTop:12, padding:10, background:'#f8f9fa', borderRadius:4, fontSize:11 }}>
                <strong>Bank Details:</strong> {inv.bank_name} | A/C: {inv.bank_account_no} | IFSC: {inv.bank_ifsc} | Branch: {inv.bank_branch}
              </div>
            )}

            {/* Footer */}
            <div className="footer" style={{ display:'flex', justifyContent:'space-between', marginTop:24 }}>
              <div style={{ fontSize:10, color:'#666' }}>
                <div>This is a computer generated invoice</div>
                {inv.notes && <div style={{ marginTop:4 }}>Notes: {inv.notes}</div>}
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ marginBottom:30 }}>For <strong>{inv.company_name}</strong></div>
                <div style={{ borderTop:'1px solid #999', paddingTop:4 }}>Authorised Signatory</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {inv.payments && inv.payments.length > 0 && (
        <div className="card no-print" style={{ marginTop: 16 }}>
          <div className="card-header"><h2>Payment History</h2></div>
          <div className="table-container">
            <table>
              <thead><tr><th>Payment #</th><th>Date</th><th>Mode</th><th>Reference</th><th style={{textAlign:'right'}}>Amount</th></tr></thead>
              <tbody>
                {inv.payments.map(p => (
                  <tr key={p.id}>
                    <td className="td-mono">{p.payment_number}</td>
                    <td>{new Date(p.payment_date).toLocaleDateString('en-IN')}</td>
                    <td><span className="badge badge-blue">{p.payment_mode}</span></td>
                    <td>{p.reference_number || '-'}</td>
                    <td className="td-amount font-bold text-success">₹{fmt(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
